import { debounce, throttle } from "throttle-debounce";
import * as React from "react";

export interface CallbackFunction {
  (inputValue: string): void;
}

export interface Options {
  /** Delay between callback calls, by default 300ms */
  delay?: number;
  /** Condition when to use throttle instead of debounce, by default used when input is short (less than 7 characters) or new word is started */
  shouldThrottle?: boolean | ((value: string) => boolean);
}

const DEFAULT_DELAY = 300;
const DEFAULT_INPUT_MIN_LENGTH = 7;
const DEFAULT_SHOULD_THROTTLE = (inputValue: string) =>
  inputValue.length < DEFAULT_INPUT_MIN_LENGTH || inputValue.endsWith(" ");

const throttleDebounceInput = (
  ref: React.MutableRefObject<[CallbackFunction, Options]>,
) => {
  const { delay = DEFAULT_DELAY, shouldThrottle = DEFAULT_SHOULD_THROTTLE } =
    ref.current[1];
  const throttledCallback = throttle(delay, (inputValue) =>
    ref.current[0](inputValue),
  );
  const debouncedCallback = debounce(delay, (inputValue) =>
    ref.current[0](inputValue),
  );

  return (inputValue: string) => {
    const condition =
      typeof shouldThrottle === "function"
        ? shouldThrottle(inputValue)
        : shouldThrottle;

    if (condition) {
      throttledCallback(inputValue);
    } else {
      debouncedCallback(inputValue);
    }
  };
};

/**
 * @param func Callback function to be debounced or throttled
 * @param options options.delay: amount of time to delay the throttle or debounce,
 *  options.shouldThrottle: boolean deciding to use throttle or debouncing.
 *  _Note: This cannot currently be changed after initial render due to memoization._
 * @returns Memoized version of the func callback wrapped in debounce/throttle methods
 * @description Within this function a ref is utilized to ensure that every call to useThrottledDebouncedInput does not re-memoize.
 *  We memoize a reference to the func and options properties being passed as parameters.
 *  Therefore, we do not want to re-memoize if the func changes, but the reference will always point
 *  to the current func value.
 */
export const useThrottledDebouncedInput = (
  func: CallbackFunction,
  options: Options = {},
) => {
  const paramsRef = React.useRef<[CallbackFunction, Options]>([func, options]);
  paramsRef.current = [func, options];

  return React.useMemo(() => throttleDebounceInput(paramsRef), []);
};
